import React, { useEffect, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { QRCodeSVG } from 'qrcode.react';
import DreamMarque from './common/DreamMarque';
import { ReactComponent as DreamWordmark } from '../icons/brand/dream-wordmark.svg';
import PublicNoticePage from './common/PublicNoticePage';
import { sendOnboardingLinkEvent } from '../lib/onboardingLinkEvents';
import './join-page.scss';

type JoinReferralData = {
  inviteType: 'reusable' | 'candidate';
  referrerFirstName: string;
  code?: string;
  intendedCandidateName?: string;
  whatsappUrl: string;
};

export const WAITLIST_URL = 'https://usedream.ai/join-pa';

const WhatsAppIcon = () => (
  <svg viewBox='0 0 24 24' fill='currentColor' aria-hidden='true'>
    <path d='M17.472 14.382c-.297-.149-1.758-.867-2.03-.967-.273-.099-.471-.148-.67.15-.197.297-.767.966-.94 1.164-.173.199-.347.223-.644.075-.297-.15-1.255-.463-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.298-.347.446-.52.149-.174.198-.298.298-.497.099-.198.05-.371-.025-.52-.075-.149-.669-1.612-.916-2.207-.242-.579-.487-.5-.669-.51-.173-.008-.371-.01-.57-.01-.198 0-.52.074-.792.372-.272.297-1.04 1.016-1.04 2.479 0 1.462 1.065 2.875 1.213 3.074.149.198 2.096 3.2 5.077 4.487.709.306 1.262.489 1.694.625.712.227 1.36.195 1.871.118.571-.085 1.758-.719 2.006-1.413.248-.694.248-1.289.173-1.413-.074-.124-.272-.198-.57-.347m-5.421 7.403h-.004a9.87 9.87 0 01-5.031-1.378l-.361-.214-3.741.982.998-3.648-.235-.374a9.86 9.86 0 01-1.51-5.26c.001-5.45 4.436-9.884 9.888-9.884 2.64 0 5.122 1.03 6.988 2.898a9.825 9.825 0 012.893 6.994c-.003 5.45-4.437 9.884-9.885 9.884m8.413-18.297A11.815 11.815 0 0012.05 0C5.495 0 .16 5.335.157 11.892c0 2.096.547 4.142 1.588 5.945L.057 24l6.305-1.654a11.882 11.882 0 005.683 1.448h.005c6.554 0 11.89-5.335 11.893-11.893a11.821 11.821 0 00-3.48-8.413z' />
  </svg>
);

const JoinPage: React.FC = () => {
  const { t } = useTranslation();
  const [searchParams] = useSearchParams();
  const refCode = searchParams.get('ref');
  const inviteToken = searchParams.get('invite');
  const apiBase = (process.env.REACT_APP_API_URL || '').replace(/\/$/, '');

  const [loading, setLoading] = useState(true);
  const [data, setData] = useState<JoinReferralData | null>(null);
  const pageViewSent = useRef(false);

  useEffect(() => {
    if (!refCode && !inviteToken) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    const validateCode = async () => {
      try {
        const query = inviteToken
          ? `invite=${encodeURIComponent(inviteToken)}`
          : `code=${encodeURIComponent(refCode || '')}`;
        const response = await fetch(`${apiBase}/join-referral?${query}`);
        const payload = await response.json().catch(() => null);

        if (!cancelled) {
          setData(response.ok && payload?.data ? payload.data : null);
        }
      } catch {
        if (!cancelled) {
          setData(null);
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    };

    validateCode();

    return () => {
      cancelled = true;
    };
  }, [apiBase, inviteToken, refCode]);

  useEffect(() => {
    if (inviteToken || !data?.code || pageViewSent.current) return;
    pageViewSent.current = true;
    sendOnboardingLinkEvent(data.code, 'page_view', 'app_join');
  }, [data?.code, inviteToken]);

  if (loading) {
    return <PublicNoticePage loading />;
  }

  if (!data) {
    return (
      <PublicNoticePage
        title={t('join.inviteOnlyTitle')}
        body={t('join.invalidBody')}
        actions={
          <a className='public-notice__button' href={WAITLIST_URL}>
            {t('join.joinWaitlist')}
          </a>
        }
      />
    );
  }

  return (
    <div className='join-page'>
      <header className='join-page__header'>
        <div className='join-page__logo' aria-hidden='true'>
          <DreamMarque className='join-page__marque' />
          <DreamWordmark className='join-page__wordmark' />
        </div>
      </header>

      <main className='join-page__content'>
        <div className='join-page__intro'>
          <div className='join-page__badge join-page__badge--whatsapp'>
            <WhatsAppIcon />
          </div>
          <h1 className='join-page__title'>
            {t('join.title')}{' '}
            <span className='join-page__title-accent'>
              {t('join.titleThanks', { name: data.referrerFirstName })}
            </span>
            .
          </h1>
          <p className='join-page__text'>{t('join.scanQr')}</p>
          <p className='join-page__text'>{t('join.fromThere')}</p>
          <a
            className='join-page__whatsapp-button'
            href={data.whatsappUrl}
            target='_blank'
            rel='noopener noreferrer'
            onClick={() => {
              if (!inviteToken && data.code) {
                sendOnboardingLinkEvent(data.code, 'whatsapp_tap', 'app_join');
              }
            }}
          >
            <WhatsAppIcon />
            {t('join.openWhatsApp')}
          </a>
        </div>
        <div className='join-page__qr-card'>
          <QRCodeSVG
            value={data.whatsappUrl}
            size={320}
            level='M'
            bgColor='transparent'
            fgColor='currentColor'
          />
        </div>
      </main>
    </div>
  );
};

export default JoinPage;
